import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Course, CourseQuestionChoice, Question, QuestionCategory } from 'src/app/shared/models/course';
import { Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { CourseService } from 'src/app/shared/services/course.service';

@Component({
  selector: 'app-course-question-crud',
  templateUrl: './course-question-crud.component.html',
  styleUrls: ['./course-question-crud.component.scss'],
})
export class CourseQuestionCrudComponent implements OnInit {

  @Input() course: Course;
  public questionForm: FormGroup;
  public types: string[] = [];
  public questionCategories: QuestionCategory[] = [];
  public allQuestionCategories: QuestionCategory[] = [];
  public maxChoices = 4;
  public submitted: boolean[] = [];

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private courseService: CourseService,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.initializeForm([]);
    this.courseService.getQuestionCategories(true).subscribe(allCategories => {
      this.allQuestionCategories = allCategories;
      this.courseService.getQuestionCategories(false).subscribe(questionCategories => {
        this.questionCategories = questionCategories;
        if (this.course?.id) {
          this.getCourse(this.course?.id);
          this.getQuestions(this.course?.id);
        }
      });
    });

    this.courseService.getQuestionTypes().subscribe(types => {
      this.types = types.filter(type => type === 'MULTIPLE_CHOICE');
    });
  }

  private initializeForm(courseQuestions: Question[]): void {
    this.questionForm = this.fb.group({
      courseQuestions: this.fb.array([]),
    });
    if (courseQuestions.length > 0) {
      courseQuestions.forEach(question => this.addNewQuestion(question));
    }
    this.addNewQuestion();
  }

  private getCourse(courseId: number) {
    this.courseService.getCourse(courseId).subscribe({
      next: (course: Course) => {
        this.course = course;
      },
      error: (error: HttpErrorResponse) => {
      },
    });
  }

  get courseQuestions(): FormArray {
    return this.questionForm.get('courseQuestions') as FormArray;
  }

  public addNewQuestion(question?: Question): void {
    const newQuestion = this.fb.group({
      courseQuestion: this.fb.group({
        id: this.fb.control(question?.courseQuestion?.id || null),
        question: this.fb.control(question?.courseQuestion?.question || '', [Validators.required]),
        notes: this.fb.control(question?.courseQuestion?.notes || ''),
        questionType: this.fb.control(question?.courseQuestion?.questionType || 'MULTIPLE_CHOICE', [Validators.required]),
        mandatory: this.fb.control(question?.courseQuestion?.mandatory || false),
        active: this.fb.control(question?.courseQuestion?.active || true),
        disabled: this.fb.control(question?.courseQuestion?.disabled || false),
        courseId: this.fb.control(question?.courseQuestion?.courseId || this.course?.id, [Validators.required]),
        courseName: this.fb.control(question?.courseQuestion?.courseName || this.course?.name),
        questionCategoryId: this.fb.control(question?.courseQuestion?.questionCategoryId || null, [Validators.required]),
        questionCategoryName: this.fb.control(question?.courseQuestion?.questionCategoryName || ''),
      }),
      courseQuestionChoices: this.fb.array([]),
    });
    if (question?.courseQuestionChoices?.length > 0) {
      question.courseQuestionChoices.forEach((choice) => {
        this.pushCourseQuestionChoice(newQuestion, choice);
      });
    } else {
      const questionType = newQuestion.get('courseQuestion.questionType')?.value;
      if (questionType === 'MULTIPLE_CHOICE') {
        this.pushCourseQuestionChoice(newQuestion);
        this.pushCourseQuestionChoice(newQuestion);
      } else {
        this.pushCourseQuestionChoice(newQuestion);
      }
    }
    this.courseQuestions.push(newQuestion);
  }

  public setForm(question?: Question): void {
    if (!question) {
      question = {
        courseQuestion: {
          id: null,
          question: '',
          notes: '',
          questionType: 'SINGLE_CHOICE',
          mandatory: false,
          active: true,
          disabled: false,
          courseId: null,
          courseName: '',
          questionCategoryId: null,
          questionCategoryName: '',
        },
        courseQuestionChoices: [],
      };
    }
    const questionGroup = this.fb.group({
      courseQuestion: this.fb.group({
        id: this.fb.control(question.courseQuestion.id),
        question: this.fb.control(question.courseQuestion.question, [Validators.required]),
        notes: this.fb.control(question.courseQuestion.notes),
        questionType: this.fb.control(question.courseQuestion.questionType, []),
        mandatory: this.fb.control(question.courseQuestion.mandatory),
        active: this.fb.control(question.courseQuestion.active),
        disabled: this.fb.control(question.courseQuestion.disabled),
        courseId: this.fb.control(question.courseQuestion.courseId || this.course?.id, []),
        courseName: this.fb.control(question.courseQuestion.courseName),
        questionCategoryId: this.fb.control(question.courseQuestion.questionCategoryId, [Validators.required]),
        questionCategoryName: this.fb.control(question.courseQuestion.questionCategoryName),
      }),
      courseQuestionChoices: this.fb.array([], []),
    });

    if (question?.courseQuestionChoices.length > 0) {
      question.courseQuestionChoices.forEach((choice) => this.pushCourseQuestionChoice(questionGroup, choice));
    } else {
      if (question.courseQuestion.questionType === 'MULTIPLE_CHOICE') {
        this.pushCourseQuestionChoice(questionGroup);
        this.pushCourseQuestionChoice(questionGroup);
      } else {
        this.pushCourseQuestionChoice(questionGroup);
      }
    }
    this.courseQuestions.push(questionGroup);
  }

  public pushCourseQuestionChoice(questionGroup: FormGroup, choice?: CourseQuestionChoice): void {
    const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
    choicesArray.push(
      this.fb.group({
        id: this.fb.control(choice?.id || null),
        choice: this.fb.control(choice?.choice || '', [Validators.required]),
        notes: this.fb.control(choice?.notes || ''),
        point: this.fb.control(choice?.point || 0, [Validators.min(0)]),
        disabled: this.fb.control(choice?.disabled || false),
        courseQuestionId: this.fb.control(choice?.courseQuestionId || null),
        correct: this.fb.control(choice?.correct || false),
      })
    );
  }

  public addChoice(questionIndex: number): void {
    const questionGroup = this.courseQuestions.at(questionIndex) as FormGroup;
    const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
    if (choicesArray.length < this.maxChoices) {
      this.pushCourseQuestionChoice(questionGroup);
    } else {
      this.toastr.error('Maximum of 4 choices allowed!');
    }
  }

  public removeChoice(questionIndex: number, choiceIndex: number): void {
    const questionGroup = this.courseQuestions.at(questionIndex) as FormGroup;
    const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
    if (choicesArray.length > 1) {
      choicesArray.removeAt(choiceIndex);
    } else {
      this.toastr.error('At least 1 choice is required!');
    }
  }

  public toggleSelection(questionIndex: number, choiceIndex: number): void {
    const questionGroup = this.courseQuestions.at(questionIndex) as FormGroup;
    const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
    choicesArray.controls.forEach((choice, index) => {
      choice.get('correct')?.setValue(index === choiceIndex);
    });
  }

  public isSelected(questionIndex: number, choiceIndex: number): boolean {
    const questionGroup = this.courseQuestions.at(questionIndex) as FormGroup;
    const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
    return choicesArray.at(choiceIndex).get('correct')?.value;
  }

  public onQuestionTypeChange(selectedType: string, questionIndex: number): void {
    const questionGroup = this.courseQuestions.at(questionIndex) as FormGroup;
    questionGroup.get('courseQuestion.questionType')?.setValue(selectedType);
    const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
    choicesArray.clear();
    if (selectedType === 'MULTIPLE_CHOICE') {
      this.pushCourseQuestionChoice(questionGroup);
      this.pushCourseQuestionChoice(questionGroup);
    } else {
      this.pushCourseQuestionChoice(questionGroup);
    }
  }

  public saveQuestion(inProgress?: any, index?: number): void {
    const questionFormGroup = this.courseQuestions.at(index) as FormGroup;
    this.submitted[index] = true;
    inProgress.value = true;
    Object.keys(questionFormGroup.controls).forEach(key => {
      const control = questionFormGroup.get(key);
      if (control instanceof FormGroup || control instanceof FormArray) {
        Object.keys(control.controls).forEach(subKey => {
          control.get(subKey)?.markAsTouched();
          control.get(subKey)?.updateValueAndValidity();
        });
      } else {
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }
    });
    if (questionFormGroup.valid) {
      const questionToSave = questionFormGroup.value;
      const categoryId = questionToSave.courseQuestion.questionCategoryId;
      const category = this.allQuestionCategories.find(c => c.id === categoryId);
      if (category) {
        questionToSave.courseQuestion.questionCategoryName = category.name;
        questionFormGroup.get('courseQuestion.questionCategoryName')?.setValue(category.name);
      }
      if (questionToSave.courseQuestion.id) {
        this.courseService.updateQuestion(questionToSave.courseQuestion.id, questionToSave).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.toastr.success(response.text);
              inProgress.value = false;
              this.submitted[index] = false;
              this.getQuestion(index);
            } else {
              this.commonService.setToastr(500, response.text);
              inProgress.value = false;
            }
          },
          error: () => {
            inProgress.value = false;
          },
        });
      } else {
        this.courseService.createQuestion(questionToSave).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const questionId = parseInt(response.payload);
              questionFormGroup.get('courseQuestion.id')?.setValue(questionId);
              this.toastr.success(response.text);
              inProgress.value = false;
              this.submitted[index] = false;
              this.addNewQuestion();
              this.getQuestion(index);
            } else {
              this.commonService.setToastr(500, response.text);
              inProgress.value = false;
            }
          },
          error: () => {
            inProgress.value = false;
          },
        });
      }
    } else {
      inProgress.value = false;
    }
  }

  private getQuestion(index: number): void {
    const questionGroup = this.courseQuestions.at(index) as FormGroup;
    const questionId = questionGroup.get('courseQuestion.id')?.value;
    if (questionId) {
      this.courseService.getQuestion(questionId).subscribe((updatedQuestion: Question) => {
        if (updatedQuestion) {
          const categoryId = updatedQuestion.courseQuestion.questionCategoryId;
          const category = this.allQuestionCategories.find(c => c.id === categoryId);
          questionGroup.patchValue({
            courseQuestion: {
              id: updatedQuestion.courseQuestion.id,
              question: updatedQuestion.courseQuestion.question,
              notes: updatedQuestion.courseQuestion.notes,
              questionType: updatedQuestion.courseQuestion.questionType,
              mandatory: updatedQuestion.courseQuestion.mandatory,
              active: updatedQuestion.courseQuestion.active,
              disabled: updatedQuestion.courseQuestion.disabled,
              courseId: updatedQuestion.courseQuestion.courseId,
              courseName: updatedQuestion.courseQuestion.courseName,
              questionCategoryId: categoryId,
              questionCategoryName: category?.name || updatedQuestion.courseQuestion.questionCategoryName,
            }
          });

          const choicesArray = questionGroup.get('courseQuestionChoices') as FormArray;
          choicesArray.clear();
          updatedQuestion.courseQuestionChoices.forEach(choice => {
            this.pushCourseQuestionChoice(questionGroup, choice);
          });
        }
      });
    }
  }

  getCategoryName(categoryId: number): string {
    const activeCategory = this.questionCategories.find(c => c.id === categoryId);
    if (activeCategory) {
      return activeCategory.name;
    }
    const allCategory = this.allQuestionCategories.find(c => c.id === categoryId);
    if (allCategory) {
      return allCategory.name;
    }
    return categoryId ? `Category #${categoryId}` : 'Select Category';
  }

  public deleteQuestion(questionIndex: number): void {
    const questionGroup = this.courseQuestions.at(questionIndex) as FormGroup;
    const questionId = questionGroup.get('courseQuestion.id')?.value;
    if (questionId) {
      this.courseService.deleteQuestion(questionId).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            this.courseQuestions.removeAt(questionIndex);
            this.toastr.success(response.text);
          } else {
            this.toastr.error(response.text);
          }
        },
        error: (error: HttpErrorResponse) => {
        },
      });
    } else {
      this.courseQuestions.removeAt(questionIndex);
    }
  }

  private getQuestions(courseId: number): void {
    if (courseId) {
      this.courseService.getQuestions(courseId).subscribe(
        (questions) => {
          const sortedQuestions = questions.sort((a, b) => (a.courseQuestion.id ?? 0) - (b.courseQuestion.id ?? 0));
          this.initializeForm(sortedQuestions);
        },
        (error) => {
        }
      );
    }
  }
  getChoiceLabel(index: number | null | undefined): string {
    if (index === null || index === undefined) return '';
    return String.fromCharCode(65 + index);
  }
}